// src/features/captions/components/CaptionsPage.tsx
//
// Live automated captions — the "Captions" surface (Media nav). Account-wide
// caption vocabulary + per-stream term sets + the AI research flow. Entirely
// invisible to unentitled accounts (useCaptionsEligibility is the launch
// gate); a direct URL hit while ineligible gets the not-available notice,
// mirroring LegacyReportsPage. Per-stream caption ON/OFF + language/position
// live in each stream's settings (StreamControlsSection), not here.

import { useState } from 'react';
import { Alert, Button, Card, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconSparkles } from '@tabler/icons-react';
import { CaptionTermUtils } from '../CaptionTermUtils';
import { useCaptionsEligibility } from '../useCaptionsEligibility';
import { useCaptionTermSets } from '../useCaptionTerms';
import {
  useDeleteAccountCaptionTerms,
  useSaveAccountCaptionTerms,
} from '../useCaptionMutations';
import { CaptionTermsEditor } from './CaptionTermsEditor';
import { GenerateTermsModal } from './GenerateTermsModal';
import { StreamTermsCard } from './StreamTermsCard';

export function CaptionsPage() {
  const { eligible } = useCaptionsEligibility();

  if (!eligible) {
    return (
      <Stack gap="md">
        <Title order={2}>Captions</Title>
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>
          Automated captions are not available for this account.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md" maw={860}>
      <Title order={2}>Captions</Title>
      <Text size="sm" c="dimmed">
        Caption vocabulary — names and terms that live captions often get wrong:
        people, places, teams, programs. Captions use these to spell them correctly.
      </Text>
      <AccountTermsCard />
      <StreamTermsCard />
    </Stack>
  );
}

function AccountTermsCard() {
  const { termSets, isLoading, isError, error } = useCaptionTermSets();
  const saveMutation = useSaveAccountCaptionTerms();
  const deleteMutation = useDeleteAccountCaptionTerms();
  const [generateOpened, { open: openGenerate, close: closeGenerate }] = useDisclosure(false);

  const accountSet = termSets.find((s) => s.scope_type === 'account');
  const serverTerms = accountSet?.terms ?? [];

  // null = not edited; the editor shows server terms until the user changes
  // something (or applies a generation draft).
  const [draft, setDraft] = useState<string[] | null>(null);
  const terms = draft ?? serverTerms;
  const isDirty = draft !== null && !CaptionTermUtils.sameTerms(draft, serverTerms);

  const handleSave = async () => {
    try {
      const res = await saveMutation.mutateAsync(terms);
      setDraft(null);
      notifications.show({
        title: 'Saved',
        message: CaptionTermUtils.formatWriteFeedback(res),
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save terms',
        color: 'red',
      });
    }
  };

  const handleClearAll = () => {
    modals.openConfirmModal({
      title: 'Clear all account terms?',
      children: (
        <Text size="sm">
          Remove all {serverTerms.length} saved term{serverTerms.length === 1 ? '' : 's'}?
          Captions keep working without them, but names may caption incorrectly.
        </Text>
      ),
      labels: { confirm: 'Clear all', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync();
          setDraft(null);
          notifications.show({ title: 'Cleared', message: 'All account terms removed.', color: 'green' });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to clear terms',
            color: 'red',
          });
        }
      },
    });
  };

  const handleApplyGenerated = (generatedTerms: string[]) => {
    const before = terms.length;
    const merged = CaptionTermUtils.normalizeTerms([...terms, ...generatedTerms]);
    setDraft(merged.terms);
    const added = merged.terms.length - before;
    notifications.show({
      title: 'Terms added to the editor',
      message:
        added > 0
          ? `${added} new term${added === 1 ? '' : 's'} added — review and click Save Terms.`
          : 'No new terms — everything was already in the editor.',
      color: 'blue',
    });
  };

  return (
    <Card shadow="sm" padding="lg" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Account Terms</Text>
            <Text size="sm" c="dimmed">Applied to captions on every stream in this account.</Text>
          </div>
          <Button
            variant="light"
            size="sm"
            leftSection={<IconSparkles size={16} />}
            onClick={openGenerate}
          >
            Generate with AI research
          </Button>
        </Group>

        {isError && (
          <Alert color="red" variant="light">
            {error instanceof Error ? error.message : 'Failed to load caption terms.'}
          </Alert>
        )}

        {isLoading ? (
          <Skeleton height={60} />
        ) : (
          <>
            <CaptionTermsEditor
              label="Terms"
              value={terms}
              onChange={setDraft}
              placeholder="Add a name or term..."
            />

            <Group justify="space-between">
              <Button
                variant="subtle"
                color="red"
                size="compact-sm"
                onClick={handleClearAll}
                disabled={serverTerms.length === 0}
                loading={deleteMutation.isPending}
              >
                Clear all
              </Button>
              <Group gap="sm">
                {isDirty && (
                  <Button variant="default" onClick={() => setDraft(null)} disabled={saveMutation.isPending}>
                    Discard
                  </Button>
                )}
                <Button onClick={handleSave} disabled={!isDirty} loading={saveMutation.isPending}>
                  Save Terms
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>

      <GenerateTermsModal
        opened={generateOpened}
        onClose={closeGenerate}
        onApply={handleApplyGenerated}
      />
    </Card>
  );
}
